import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, X, MagnifyingGlass, User, GearSix, SignOut, BookmarkSimple, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { FarmwatchLogo } from "@/components/FarmwatchLogo";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [marketsMenuOpen, setMarketsMenuOpen] = useState(false);
  const [newsMenuOpen, setNewsMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const navigate = useNavigate();

  const { user, logout, isAdmin } = useAuth();

  const navLinks = [
    { label: "News", href: "#", hasDropdown: true, dropdownType: "news" },
    { label: "Marts & Markets", href: "#", hasDropdown: true, dropdownType: "markets" },
    { label: "Buy/Sell", href: "/marketplace" },
    { label: "Land", href: "/land" },
    { label: "Jobs", href: "/jobs" },
    { label: "Grants", href: "/grants" },
    { label: "Shop", href: "/shop" },
    { label: "Community", href: "/community" },
  ];

  const newsLinks = [
    { label: "All News", href: "/all-news", flag: "📰" },
    { label: "Irish Farming News", href: "/irish-news", flag: "🇮🇪" },
    { label: "UK Farming News", href: "/uk-news", flag: "🇬🇧" },
    { label: "US Farming News", href: "/us-news", flag: "🇺🇸" },
    { label: "Global Farming News", href: "/global-news", flag: "🌍" },
  ];

  const marketLinks = [
    { label: "Today's Prices", href: "/todays-mart-prices", flag: "🔴" },
    { label: "Ireland", href: "/irish-marts", flag: "🇮🇪" },
    { label: "UK", href: "/uk-markets", flag: "🇬🇧" },
    { label: "USA", href: "/usa-markets", flag: "🇺🇸" },
    { label: "Price Alerts", href: "/alerts", flag: "🔔" },
  ];

  const handleNavClick = (e, link) => {
    if (link.hasDropdown) {
      e.preventDefault();
      if (link.dropdownType === "news") {
        setNewsMenuOpen(!newsMenuOpen);
        setMarketsMenuOpen(false);
      } else if (link.dropdownType === "markets") {
        setMarketsMenuOpen(!marketsMenuOpen);
        setNewsMenuOpen(false);
      }
    }
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  useEffect(() => {
    const onOpen = (e) => openAuthModal(e.detail || "login");
    window.addEventListener("farmwatch:open-auth", onOpen);
    return () => window.removeEventListener("farmwatch:open-auth", onOpen);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <>
      <header className="bg-[#FFF1E5] border-b border-[#E0D4C8] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <FarmwatchLogo size={40} variant="full" darkMode={false} />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <div key={link.label} className="relative">
                  {link.hasDropdown ? (
                    <>
                      <button
                        onClick={(e) => handleNavClick(e, link)}
                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center gap-1 text-sm font-sans text-[#6B6661] hover:text-[#0F766E] transition-colors cursor-pointer"
                      >
                        {link.label}
                        <CaretDown size={14} className={`transition-transform ${
                          (link.dropdownType === "news" && newsMenuOpen) || 
                          (link.dropdownType === "markets" && marketsMenuOpen) 
                            ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {/* News Dropdown */}
                      {link.dropdownType === "news" && newsMenuOpen && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-[#E0D4C8] shadow-lg z-50">
                          {newsLinks.map((news) => (
                            <Link
                              key={news.label}
                              to={news.href}
                              onClick={() => setNewsMenuOpen(false)}
                              data-testid={`nav-news-${news.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-[#33302E] hover:bg-[#F0FDF4] hover:text-[#0F766E] transition-colors"
                            >
                              <span>{news.flag}</span>
                              {news.label}
                            </Link>
                          ))}
                        </div>
                      )}
                      
                      {/* Markets Dropdown */}
                      {link.dropdownType === "markets" && marketsMenuOpen && (
                        <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-[#E0D4C8] shadow-lg z-50">
                          {marketLinks.map((market) => (
                            <Link
                              key={market.label}
                              to={market.href}
                              onClick={() => setMarketsMenuOpen(false)}
                              data-testid={`nav-market-${market.label.toLowerCase()}`}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-[#33302E] hover:bg-[#F0FDF4] hover:text-[#0F766E] transition-colors"
                            >
                              <span>{market.flag}</span>
                              {market.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={(e) => handleNavClick(e, link)}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      className="text-sm font-sans text-[#6B6661] hover:text-[#0F766E] transition-colors cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <button
                data-testid="search-toggle"
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-[#6B6661] hover:text-[#0F766E] transition-colors"
              >
                <MagnifyingGlass size={20} />
              </button>

              {/* User Menu / Auth */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu-toggle"
                    className="flex items-center gap-2 px-3 py-2 border border-[#E0D4C8] hover:border-[#0F766E] transition-colors"
                  >
                    <User size={18} className="text-[#0F766E]" />
                    <span className="hidden sm:inline text-sm font-sans text-[#33302E]">
                      {user.name.split(' ')[0]}
                    </span>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-[#E0D4C8] shadow-lg z-50">
                      <div className="px-4 py-3 border-b border-[#E0D4C8]">
                        <p className="font-sans text-sm font-medium text-[#33302E]">{user.name}</p>
                        <p className="text-xs text-[#6B6661]">{user.email}</p>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="user-dashboard-link"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-[#33302E] hover:bg-[#F6E9DD] transition-colors"
                      >
                        <BookmarkSimple size={16} />
                        My Dashboard
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="admin-link"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#33302E] hover:bg-[#F6E9DD] transition-colors"
                        >
                          <GearSix size={16} />
                          Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          logout();
                          setUserMenuOpen(false);
                        }}
                        data-testid="logout-btn"
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                      >
                        <SignOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={() => openAuthModal("login")}
                    data-testid="login-btn"
                    className="px-4 py-2 text-sm font-sans text-[#33302E] hover:text-[#0F766E] transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => openAuthModal("register")}
                    data-testid="signup-btn"
                    className="px-4 py-2 bg-[#111827] text-white text-sm font-sans hover:bg-[#0F766E] transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}

              {/* Mobile Menu Toggle */}
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-[#6B6661]"
              >
                {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          {searchOpen && (
            <form onSubmit={handleSearch} className="py-4 border-t border-[#E0D4C8]">
              <div className="relative">
                <MagnifyingGlass
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]"
                />
                <input
                  type="text"
                  data-testid="search-input"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-24 py-2 border border-[#E0D4C8] bg-white font-sans text-sm focus:outline-none focus:border-[#0F766E]"
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1 bg-[#111827] text-white text-sm hover:bg-[#0F766E] transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E0D4C8] bg-white">
            <nav className="max-w-[1400px] mx-auto px-4 py-4">
              {/* News Section */}
              <div className="py-3 border-b border-[#E0D4C8]">
                <span className="text-xs font-mono uppercase tracking-wider text-[#0F766E] mb-2 block">News</span>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {newsLinks.map((news) => (
                    <Link
                      key={news.label}
                      to={news.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-2 px-3 bg-[#F6E9DD] text-[#33302E] text-sm"
                    >
                      <span>{news.flag}</span>
                      {news.label}
                    </Link>
                  ))}
                </div>
              </div>
              {/* Markets Section */}
              <div className="py-3 border-b border-[#E0D4C8]">
                <span className="text-xs font-mono uppercase tracking-wider text-[#0F766E] mb-2 block">Markets by Country</span>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {marketLinks.map((market) => (
                    <Link
                      key={market.label}
                      to={market.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-2 px-3 bg-[#F6E9DD] text-[#33302E] text-sm"
                    >
                      <span>{market.flag}</span>
                      {market.label}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/grants"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[#33302E] font-sans border-b border-[#E0D4C8]"
              >
                Grants
              </Link>
              <Link
                to="/community"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[#33302E] font-sans border-b border-[#E0D4C8]"
              >
                Community
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[#33302E] font-sans border-b border-[#E0D4C8]"
              >
                Pricing
              </Link>
              {!user ? (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      openAuthModal("login");
                    }}
                    className="flex-1 py-2 border border-[#111827] text-[#33302E] text-center font-sans"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      openAuthModal("register");
                    }}
                    className="flex-1 py-2 bg-[#111827] text-white text-center font-sans"
                  >
                    Sign Up
                  </button>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 px-4 bg-[#F6E9DD] text-[#33302E] text-center font-sans"
                  >
                    My Dashboard
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 px-4 bg-[#0F766E] text-white text-center font-sans"
                    >
                      Admin Panel
                    </Link>
                  )}
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
      />
    </>
  );
}
